<script setup lang="ts">
import { ROOT_URL_HOST } from '@/shared/constants/contactConstants';
import AppBreadcrumbs from '@/shared/ui/AppBreadcrumbs/index';
import ChiefEditorCard from '@/widgets/ChiefEditorCard/index';
import AuthorsExperts from '@/pages/editorsStaff/ui/AuthorsExperts.vue';
import EditorsStaffSkeleton from '@/pages/editorsStaff/ui/Skeleton/index.vue';
import { useEditorsStaff } from '@/pages/editorsStaff/composables/useEditorsStaff';
import { getErrorStatus } from '@/shared/utils/getErrorStatus';
import type { Breadcrumb } from '@/shared/ui/AppBreadcrumbs/model/types/appBreadcrumbs';

const REDACTION_PAGE_TITLE = 'Редакция журнала Юрик.ру — авторы и эксперты';
const REDACTION_PAGE_DESCRIPTION =
  'Материалы журнала Юрик.ру готовят практикующие юристы и адвокаты. Знакомьтесь с главным редактором и экспертами редакции, их специализацией и рейтингом.';

const isMobile: boolean = useIsMobile();
const route = useRoute();

const { chiefEditor, authors, isLoading, isShowMore, loadMore, pageReady, pageError } =
  useEditorsStaff();

if (import.meta.server) {
  await pageReady;
  if (pageError.value) {
    throw createError({ statusCode: getErrorStatus(pageError.value), fatal: true });
  }
}

const breadcrumbs: Breadcrumb[] = [
  { id: 1, text: 'Главная', to: '/' },
  { id: 2, text: 'Редакция' },
];

const url = computed(() => `${ROOT_URL_HOST}${route.path}`);

useSeoMeta({
  title: REDACTION_PAGE_TITLE,
  description: REDACTION_PAGE_DESCRIPTION,
  ogTitle: REDACTION_PAGE_TITLE,
  ogDescription: REDACTION_PAGE_DESCRIPTION,
  ogSiteName: 'Сервис по подбору юристов, адвокатов - Юрик.ру',
  ogType: 'website',
  ogUrl: url,
  ogLocale: 'ru',
  ogImage: `${ROOT_URL_HOST}/og-image.png`,
});

useHead({
  meta: [{ property: 'vk:image', content: `${ROOT_URL_HOST}/og-image.png` }],
});
</script>

<template>
  <div
    :class="['editors-staff-page', { _mobile: isMobile }]"
    data-testid="editors-staff-page"
  >
    <EditorsStaffSkeleton v-if="isLoading" />
    <div v-else class="editors-staff-page__content">
      <div class="editors-staff-page__breadcrumbs">
        <AppBreadcrumbs :breadcrumbs="breadcrumbs" />
      </div>
      <div class="editors-staff-page__header">
        <h1 class="editors-staff-page__title">Редакция журнала Юрик.ру</h1>
        <div class="editors-staff-page__description">
          Материалы журнала готовят практикующие юристы и адвокаты. Редакция следит за
          актуальностью каждого материала и доступностью для читателя.
        </div>
      </div>
      <div class="editors-staff-page__chief-editor">
        <div class="editors-staff-page__title _chief-editor">Главный редактор</div>
        <ChiefEditorCard :data="chiefEditor" />
      </div>
      <div class="editors-staff-page__authors-experts">
        <div class="editors-staff-page__title _authors-experts">Авторы-эксперты</div>
        <AuthorsExperts :authors="authors" :is-show-more="isShowMore" @load="loadMore" />
      </div>
    </div>
  </div>
</template>

<style lang="less">
.editors-staff-page {
  .container-d();

  padding-bottom: 100px;

  &__content {
    .flex-column(@size-x10);

    max-width: 880px;
  }

  &__breadcrumbs {
    margin-top: @size-x10;
  }

  &__header {
    .flex-column(@size-x3);
  }

  &__title {
    font-size: @size-x9;
    line-height: 130%;
    font-weight: 700;

    &._chief-editor,
    &._authors-experts {
      font-size: 30px;
    }
  }

  &__description {
    font-size: @size-x5;
    line-height: 130%;
    font-weight: 700;
    color: @color-silver-chalice;
  }

  &__chief-editor,
  &__authors-experts {
    .flex-column(@size-x10);
  }

  &._mobile {
    .container-m();

    padding-bottom: 80px;
  }

  &._mobile &__content {
    .flex-column(@size-x8);

    max-width: 100%;
  }

  &._mobile &__chief-editor,
  &._mobile &__authors-experts {
    .flex-column(@size-x6);
  }
}
</style>
